import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { extractSessionFromURL } from './extract';
import { cleanSessionFromURL } from './cleanURL';
import { clearToken, loadToken, saveToken, type TokenStore } from './storage';
import { SessionContext, type SessionContextValue } from './SessionContext';
import type { SessionToken } from './types';

export function SessionProvider({ children }: { children: ReactNode }) {
  // Parsed once per mount. `session_token` in the URL wins over `embed_key`
  // (both may be present on hosted-Stripe re-entry).
  const extracted = useMemo(() => extractSessionFromURL(window.location.href), []);
  const embedKey = extracted.embedKey;

  // URL-driven and fixed for the page's lifetime — the host picks the store
  // when it opens the iframe.
  const store = useMemo<TokenStore>(
    () => ({ mode: extracted.storageMode, embedKey: extracted.embedKey }),
    [extracted],
  );

  const [token, setTokenState] = useState<SessionToken | null>(() => {
    if (extracted.token) {
      saveToken(extracted.token, store);
      cleanSessionFromURL();
      return extracted.token;
    }
    const stored = loadToken(store);
    // Re-save what we found: in `local` mode a token restored from the per-tab
    // fallback migrates into the persistent store, so the NEXT launch finds it
    // too. A no-op when it already came from the right place.
    if (stored) saveToken(stored, store);
    return stored;
  });

  const tokenRef = useRef(token);
  tokenRef.current = token;

  const getToken = useCallback(() => tokenRef.current, []);

  const adoptToken = useCallback(
    (t: SessionToken) => {
      saveToken(t, store);
      tokenRef.current = t;
      setTokenState(t);
    },
    [store],
  );

  const dropToken = useCallback(() => {
    clearToken(store);
    tokenRef.current = null;
    setTokenState(null);
  }, [store]);

  const value = useMemo<SessionContextValue>(
    () => ({
      token,
      gameId: extracted.gameId,
      embedKey,
      getToken,
      adoptToken,
      dropToken,
    }),
    [token, extracted.gameId, embedKey, getToken, adoptToken, dropToken],
  );

  return <SessionContext value={value}>{children}</SessionContext>;
}
